import type { CSSProperties, ReactNode } from 'react';
import { Alert } from 'antd';
import { useTranslation } from 'react-i18next';

/**
 * ============================================================================
 * THE LIST, AND THE BREAKPOINT DECISION IT CARRIES
 * ============================================================================
 *
 * Six of this wave's seven screens are lists — charges, payments, promises, installment
 * steps, documents, accounts — and a subscriber reads them on a handset. That makes the
 * table the hard part of «mobile-first, usable at 360 px», so the decision is made ONCE,
 * here, rather than six times:
 *
 *   **below 720 px each row is a CARD**: one labelled line per field, stacked, full width;
 *   **at 720 px and above the same rows become a GRID** with a header row, labels hidden.
 *
 * ONE markup produces both. The alternative — a `<table>` for wide screens and a card list
 * for narrow ones — means every column exists twice, and this project's most repeated review
 * finding is «one door of a pair guarded and its neighbour not». Two markups are two doors:
 * the day somebody adds a «Комиссия» column, the card list keeps the old five fields and
 * nobody notices, because each half looks right on its own screen.
 *
 * A horizontally scrolling real table was the other candidate and is rejected on the
 * evidence this portal is built on: the reader who MUST succeed here is a SUSPENDED
 * subscriber inside the walled garden, on a phone, looking for one figure. A table he has to
 * drag sideways to read hides the «Сумма» column behind the gesture.
 *
 * ARIA roles carry the semantics the divs give up (`table` / `row` / `columnheader` /
 * `cell`), so the structure survives for a screen reader and is queryable in tests. Note
 * that on each viewport exactly ONE labelling channel is visible — the header row above 720
 * px, the per-cell labels below it — because `display: none` hides from assistive technology
 * too, which is what keeps a screen reader from hearing every label twice.
 */

export interface Column<R> {
  /** Stable key — also the React key of the cell. */
  key: string;
  /** i18n key of the header and of the per-row label. Never a literal (ST13). */
  labelKey: string;
  render: (row: R) => ReactNode;
  /** The grid track for this column at ≥720 px, e.g. "1fr" or "minmax(0, 2fr)". */
  track?: string;
  /** Extra class on the cell — `pa-td-right` for a money column. */
  className?: string;
}

export function DataTable<R>({
  columns,
  rows,
  rowKey,
  emptyKey,
  detail,
  rowClass,
}: {
  columns: ReadonlyArray<Column<R>>;
  rows: readonly R[];
  rowKey: (row: R) => string | number;
  /** i18n key of the «nothing here» message. An empty list is a fact, not a failure. */
  emptyKey: string;
  /** An optional full-width line under the cells — a correction note, a schedule. */
  detail?: (row: R) => ReactNode;
  rowClass?: (row: R) => string;
}) {
  const { t } = useTranslation();

  if (rows.length === 0) {
    return <Alert type="info" showIcon message={t(emptyKey)} />;
  }

  const tracks = columns.map((c) => c.track ?? 'minmax(0, 1fr)').join(' ');
  const style = { ['--pa-table-cols' as string]: tracks } as CSSProperties;

  return (
    <div className="pa-table" role="table" style={style}>
      <div className="pa-table-head" role="row">
        {columns.map((c) => (
          <div key={c.key} role="columnheader" className={cellClass('pa-th', c.className)}>
            {t(c.labelKey)}
          </div>
        ))}
      </div>
      {rows.map((row) => (
        <div key={rowKey(row)} className={`pa-table-row ${rowClass?.(row) ?? ''}`} role="row">
          {columns.map((c) => (
            <div key={c.key} role="cell" className={cellClass('pa-td', c.className)}>
              <span className="pa-td-label">{t(c.labelKey)}</span>
              <span className="pa-td-value">{c.render(row)}</span>
            </div>
          ))}
          {detail ? <div className="pa-table-detail">{detail(row)}</div> : null}
        </div>
      ))}
    </div>
  );
}

function cellClass(base: string, extra?: string): string {
  return extra ? `${base} ${extra}` : base;
}

/**
 * FactList is the label/value block the non-list screens use — the balance card, the
 * account state, the money breakdown.
 *
 * It is the same shape as the card a DataTable row collapses into on a phone, so the two
 * read alike, and it is one component so that «Зарезервировано» and «Доступно» cannot end
 * up typeset differently on two screens.
 */
export function FactList({
  facts,
}: {
  facts: ReadonlyArray<{ key: string; label: string; value: ReactNode; hint?: ReactNode }>;
}) {
  return (
    <div className="pa-facts">
      {facts.map((f) => (
        <div className="pa-fact" key={f.key}>
          <span className="pa-fact-key">{f.label}</span>
          <span className="pa-fact-value">{f.value}</span>
          {f.hint ? <span className="pa-fact-hint">{f.hint}</span> : null}
        </div>
      ))}
    </div>
  );
}
